import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { BlockActionType } from '@app/core/enums';
import { FilterType, ViewPart } from '@app/core/models';
import { ButtonHandlerService } from '@app/core/services/button-handler.service';
import { LocalStorageService } from '@app/shared/services/storage';
import { Block } from '@core/models/block/block.model';
import { AdvancedFiltersService } from '@shared/services/filters/advanced-filters.service';
import { AdvancedFilterData, UpdateAvailableFiltersReason } from '@shared/services/filters/types';
import { camelCase } from 'lodash';
import { Subscription } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';

@Component({
  selector: 'app-advanced-filter-group-selector',
  styleUrls: ['./advanced-filter-group-selector.component.scss'],
  template: `
    <div #filterGroupSelector class="filter-group-selector" [formGroup]="form">
      <div class="header" [class]="headerTemplate.uId">
        <div app-block
             *ngIf="!searchEnabled"
             [item]="headerTemplate"
             [appClickableBlock]="headerTemplate"
             [attr.tabindex]="block.tabindex"
             (click)="onClickLabel()"
             (keyup.enter)="onClickLabel()"
             [ngStyle]="{cursor: canCursorPointerOnLabel ? 'pointer' : null}">
        </div>
        <div class="filter-container" *ngIf="searchEnabled">
          <input type="text" class="search-filter" [formControl]="searchPhrase">
          <mat-icon (click)="disableSearch()"
                    [attr.tabindex]="block.tabindex">close
          </mat-icon>
        </div>
      </div>
      @if (itemsTree.length) {
        <div [class]="itemsContainerTemplate.uId"
             [attr.tabindex]="block .tabindex"
             [ngStyle]="{cursor: itemsLoaded ? 'pointer' : null}">

          @for (item of itemsTree; track item.id; let idx = $index) {
            @if (item.display) {
              <advanced-filter-select-item
                [templates]="itemsTemplates"
                [level]="0"
                [index]="idx.toString()"
                [item]="item"
                [controlType]="controlType"
                [form]="getItemForm(item.id)"
                [itemsFormMap]="itemsFormMap"
                [ngStyle]="{display: item.display ? 'block' : 'none'}"
                (checked)="checkedOption($event)"
              >
              </advanced-filter-select-item>
            }
          }
        </div>
      }
    </div>

  `,
  providers: [ButtonHandlerService],
  standalone: false,
})
export class AdvancedFilterGroupSelectorComponent implements OnChanges, OnInit, OnDestroy {
  form: FormGroup;
  @Input() block: Block;
  itemsContainerTemplate: Block;
  itemsTemplates: Block[];
  headerTemplate: Block;
  items: AdvancedFilterData[] = [];
  itemsTree: AdvancedFilterData[] = [];
  itemsFormMap = new Map<number, FormGroup>();
  private itemsById = new Map<number, AdvancedFilterData>();
  itemsLoaded = false;
  private _filterSubscription = new Subscription();
  searchEnabled = false;
  searchPhrase = new FormControl('');

  constructor(
    private element: ElementRef,
    private _fb: UntypedFormBuilder,
    public localStorageService: LocalStorageService,
    private buttonHandlerService: ButtonHandlerService,
    private advanceFilterService: AdvancedFiltersService,
  ) {
  }

  get canCursorPointerOnLabel(): boolean {
    return this.controlType !== 'availability' && this.itemsLoaded;
  }

  get controlType(): FilterType {
    return this.getControlType(this.block);
  }

  private _expanded = false;

  get expanded(): boolean {
    return this._expanded;
  }

  set expanded(expanded: boolean) {
    // Only emit events and update the internal value if the value changes.
    if (this._expanded !== expanded) {
      this._expanded = expanded;
    }
  }

  checkedOption(event: { id: number; active: boolean }) {
    const {id, active} = event;
    const groupType = camelCase(this.controlType);
    const formGroup = this.getItemForm(id);
    if (formGroup) {
      formGroup.patchValue({active: active});
    }
    const items = this.items.map(item => {
      if (item.id === id) {
        item.active = active;
      }
      return item;
    });
    const allFilters = this.advanceFilterService.getAvailableFiltersValue();
    allFilters[groupType] = items;
    this.advanceFilterService.setFilters(
      {filters: allFilters},
      UpdateAvailableFiltersReason.userAction,
    );
  }

  log(msg, data:any = []) {
    if (this.controlType === 'availability')
      console.log(msg, structuredClone(data));
  }

  ngOnInit(): void {
    this._filterSubscription.add(this.advanceFilterService.availableFiltersUpdated$.subscribe(value => {
      this.disableSearch();
      const filters = value.data;
      const groupType = camelCase(this.controlType);
      const newItems = (filters[groupType] || []).map(item => ({...item}));

      const structureChanged = newItems.length !== this.items.length ||
        newItems.some((item, i) => item.id !== this.items[i]?.id);

      if (structureChanged) {
        this.items = newItems;
        this.itemsTree = this.buildTree(this.items);
        this.rebuildItemIndexes();
      }

      if (structureChanged) {
        this.itemsFormMap.clear();
        const formArray = this._fb.array(
          this.items.map((item) => {
            const formGroup = this._fb.group({
              id: this._fb.control(item.id),
              name: this._fb.control(item.name),
              active: this._fb.control(item.active),
              count: this._fb.control(item.count),
              disabled: this._fb.control(item.disabled),
            });
            this.itemsFormMap.set(item.id, formGroup);
            return formGroup;
          }),
        );
        this.form = this._fb.group({[groupType]: formArray});
      } else {
        this.applyItemUpdates(newItems);
      }

      this.itemsLoaded = true;
    }));
    this._filterSubscription.add(this.buttonHandlerService.click$
      .pipe(
        filter(({actionType}) => actionType === BlockActionType.CLICK),
        tap(() => this.toggleExpand()),
      )
      .subscribe());
    this._filterSubscription.add(this.searchPhrase.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => this.searchFilter()),
      )
      .subscribe());
  }

  onClickLabel() {
    if (this.controlType === 'availability') {
      return;
    }
    this.searchEnabled = !this.searchEnabled;
    if (this.searchEnabled) {
      setTimeout(() => {
        this.element.nativeElement.querySelector('input.search-filter').focus();
      }, 10);
    }
  }

  disableSearch() {
    const shouldRefreshTree = this.searchEnabled || !!this.searchPhrase.value;
    this.searchEnabled = false;
    this.searchPhrase.setValue('', {emitEvent: false});
    if (shouldRefreshTree) {
      this.searchFilter();
    }
  }

  searchFilter() {
    const value = this.searchPhrase.value;
    this.items = this.items.map(item => {
      if (value) {
        const regex = new RegExp(value, 'i');
        item.display = regex.test(item.name);
      } else {
        item.display = true;
      }
      return item;
    });
    this.itemsTree = this.buildTree(this.items);
  }

  ngOnChanges(): void {
    if (!this.block) {
      return;
    }

    if (!this.initializeHeaderViewPart(ViewPart.HEADER_TEMPLATE)) {
      throw new Error('header template missing');
    }

    if (!this.initializeItemViewPart(ViewPart.ITEM_TEMPLATE)) {
      throw new Error('item template missing');
    }

    if (!this.initializeItemsLevelsViewPart()) {
      throw new Error('items levels template missing');
    }

    const groupType = this.getControlType(this.block);
    this.form = this._fb.group({
      [groupType]: this._fb.array([]),
    });
    this.form = this._fb.group({
      [groupType]: this._fb.array(this.items.map((item) => this._fb.group({
        id: this._fb.control(item.id),
        name: this._fb.control(item.name),
        active: this._fb.control(false),
        count: this._fb.control(item.count),
        disabled: this._fb.control(item.disabled),
      }))),
    });
  }

  getItemForm(itemId: number): FormGroup {
    return this.itemsFormMap.get(itemId);
  }

  ngOnDestroy(): void {
    this._filterSubscription.unsubscribe();
  }

  private getControlType = (block: Block): FilterType => {
    const {
      settings: {controlType},
    } = block;
    return controlType;
  };

  private toggleExpand(): void {
    this.expanded = !this.expanded;
  }

  private applyItemUpdates(newItems: AdvancedFilterData[]): void {
    newItems.forEach(newItem => {
      const currentItem = this.itemsById.get(newItem.id);
      const formGroup = this.itemsFormMap.get(newItem.id);

      if (!currentItem || !formGroup) {
        return;
      }

      this.patchControlIfChanged(formGroup, 'name', newItem.name);
      this.patchControlIfChanged(formGroup, 'active', newItem.active);
      this.patchControlIfChanged(formGroup, 'count', newItem.count);
      this.patchControlIfChanged(formGroup, 'disabled', newItem.disabled);

      currentItem.active = newItem.active;
      currentItem.count = newItem.count;
      currentItem.disabled = newItem.disabled;
      currentItem.name = newItem.name;
    });
  }

  private patchControlIfChanged(formGroup: FormGroup, controlName: string, value: any): void {
    const control = formGroup.get(controlName);

    if (control && control.value !== value) {
      control.patchValue(value);
    }
  }

  private rebuildItemIndexes(): void {
    this.itemsById.clear();
    this.items.forEach(item => this.itemsById.set(item.id, item));
  }

  private initializeHeaderViewPart(name: ViewPart) {
    this.headerTemplate = this.block.children.find(
      ({settings: {viewPart}}) => viewPart === name,
    );
    return this.headerTemplate;
  }

  private initializeItemViewPart(name: ViewPart) {
    this.itemsContainerTemplate = this.block.children.find(
      ({settings: {viewPart}}) => viewPart === name,
    );
    return this.itemsContainerTemplate;
  }

  private initializeItemsLevelsViewPart() {
    this.itemsTemplates = this.itemsContainerTemplate.children.filter(
      ({settings: {viewPart}}) => viewPart.startsWith('templateLevel'),
    );
    return this.itemsTemplates.length > 0;
  }

  private buildTree(items: AdvancedFilterData[]): AdvancedFilterData[] {
    const itemMap = new Map<number, AdvancedFilterData>();
    const roots: AdvancedFilterData[] = [];

    // Utrzymujemy te same referencje itemów, żeby aktualizacja liczników nie
    // przebudowywała całego drzewa komponentów.
    items.filter(i => i.display).forEach(item => {
      item.children = [];
      itemMap.set(item.id, item);
    });

    // Zbuduj drzewo
    itemMap.forEach(item => {
      if (item.parentId === null || item.parentId === undefined) {
        roots.push(item);
      } else {
        const parent = itemMap.get(item.parentId);
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(item);
        } else {
          // Jeśli nie ma parenta, dodaj do root
          roots.push(item);
        }
      }
    });

    // AT-9043: sortowanie alfabetyczne A-Z osobno na każdym poziomie hierarchii
    this.sortTreeByName(roots);

    return roots;
  }

  /**
   * Sortuje węzły drzewa filtrów alfabetycznie po nazwie, rekurencyjnie —
   * każdy poziom (rodzeństwo) osobno. `numeric` zapewnia poprawną kolejność
   * także dla nazw z liczbami (np. "lata 20." przed "lata 30.").
   */
  private sortTreeByName(nodes: AdvancedFilterData[]): void {
    nodes.sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, {numeric: true, sensitivity: 'base'}),
    );
    nodes.forEach(node => {
      if (node.children?.length) {
        this.sortTreeByName(node.children);
      }
    });
  }
}
